// Date utilities

export const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

export const addDays = (dateStr, n) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

export const addMonths = (dateStr, n) => {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

export const addYears = (dateStr, n) => {
  const d = new Date(dateStr);
  d.setFullYear(d.getFullYear() + n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

export const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  const today = todayStr();
  const tomorrow = addDays(today, 1);
  if (dateStr === today) return 'I dag';
  if (dateStr === tomorrow) return 'I morgen';
  const diff = Math.round((d - new Date(today)) / 86400000);
  if (diff < 0) return `${Math.abs(diff)} dage forsinket`;
  if (diff < 7) return `Om ${diff} dage`;
  const months = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  return `${d.getDate()}. ${months[d.getMonth()]}`;
};

// Helper: extract a normalized weekdays array from a task
// Supports both new `weekdays` array AND old single `weekday` field for backward compat.
export const getTaskWeekdays = (task) => {
  if (Array.isArray(task.weekdays) && task.weekdays.length > 0) {
    return [...task.weekdays].map(Number).sort((a, b) => a - b);
  }
  if (Array.isArray(task.repeatWeekdays) && task.repeatWeekdays.length > 0) {
    return [...task.repeatWeekdays].map(Number).sort((a, b) => a - b);
  }
  if (task.weekday !== undefined && task.weekday !== null) {
    return [Number(task.weekday)];
  }
  return [1]; // fallback to Monday
};

// Find the next matching weekday strictly AFTER the given date.
// `targets` is an array of weekday numbers (0=Sunday ... 6=Saturday).
const nextMatchingWeekday = (dateStr, targets) => {
  if (!targets || targets.length === 0) return null;
  const targetSet = new Set(targets.map(Number));
  let d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  let safety = 0;
  while (!targetSet.has(d.getDay()) && safety < 14) {
    d.setDate(d.getDate() + 1);
    safety++;
  }
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

// Find the next matching weekday on OR AFTER the given date.
// Used to "snap" a starting point to the first valid occurrence.
const snapToMatchingWeekday = (dateStr, targets) => {
  if (!targets || targets.length === 0) return dateStr;
  const targetSet = new Set(targets.map(Number));
  let d = new Date(dateStr);
  let safety = 0;
  while (!targetSet.has(d.getDay()) && safety < 14) {
    d.setDate(d.getDate() + 1);
    safety++;
  }
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

// Get the next occurrence of a recurring task strictly AFTER currentDate.
// For weekday repeats, supports an array of multiple weekdays.
export const getNextDueDate = (currentDate, repeatType, interval, monthDay, weekday, weekdays) => {
  switch (repeatType) {
    case 'daily': return addDays(currentDate, 1);
    case 'every_n_days': return addDays(currentDate, interval || 1);
    case 'weekday': {
      // Prefer multi-weekday array if provided; fall back to single weekday number.
      let targets;
      if (Array.isArray(weekdays) && weekdays.length > 0) {
        targets = weekdays.map(Number);
      } else if (weekday !== undefined && weekday !== null) {
        targets = [Number(weekday)];
      } else {
        targets = [1]; // Monday default
      }
      return nextMatchingWeekday(currentDate, targets);
    }
    case 'weekly': return addDays(currentDate, 7);
    case 'every_n_weeks': return addDays(currentDate, 7 * (interval || 1));
    case 'monthly': return addMonths(currentDate, 1);
    case 'monthly_date': {
      const d = new Date(currentDate);
      d.setMonth(d.getMonth() + 1);
      d.setDate(monthDay || d.getDate());
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    }
    case 'yearly': return addYears(currentDate, 1);
    default: return null;
  }
};

export const describeRepeat = (task) => {
  if (!task.repeatType || task.repeatType === 'none') return null;
  switch (task.repeatType) {
    case 'daily': return 'Hver dag';
    case 'every_n_days': return `Hver ${task.interval}. dag`;
    case 'weekday': {
      const days = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
      const targets = getTaskWeekdays(task);
      if (targets.length === 1) return `Hver ${days[targets[0]] || 'uge'}`;
      // Multi-day: list them
      const labels = targets.map(d => days[d]).filter(Boolean);
      if (labels.length === 7) return 'Hver dag';
      // Special case: weekdays only
      if (labels.length === 5 && targets.includes(1) && targets.includes(2) && targets.includes(3) && targets.includes(4) && targets.includes(5)) {
        return 'Hverdage (man-fre)';
      }
      // Special case: weekend only
      if (labels.length === 2 && targets.includes(0) && targets.includes(6)) {
        return 'Weekender';
      }
      return labels.join(', ');
    }
    case 'weekly': return 'Hver uge';
    case 'every_n_weeks': return `Hver ${task.interval}. uge`;
    case 'monthly': return 'Hver måned';
    case 'monthly_date': return `Hver måned d. ${task.monthDay}`;
    case 'yearly': return 'Hvert år';
    default: return null;
  }
};

// Calculate points based on deadline timing.
// Points are ONLY earned for tasks received from a partner (list='sent' assigned to me).
// Private and shared tasks completed by yourself give 0 points (just a celebration).
export const calculatePoints = (task, completionISO) => {
  if (task.list !== 'sent') return 0;
  const basePoints = task.tagPoints || 5;
  if (!task.deadline) return basePoints;

  const deadline = new Date(task.deadline);
  const completed = new Date(completionISO);
  const diffMs = deadline - completed;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < 0) return Math.ceil(basePoints / 2);
  if (diffDays > 1) return Math.ceil(basePoints * 1.5);
  return basePoints;
};

// Infer a starting date for recurring tasks that don't have an explicit deadline.
// For weekday-specific repeats, snaps forward to the first matching weekday.
export const inferRecurringStart = (task) => {
  if (!task.repeatType || task.repeatType === 'none') return null;

  let anchor;
  if (task.createdAt) {
    anchor = task.createdAt.split('T')[0];
  } else {
    anchor = todayStr();
  }

  switch (task.repeatType) {
    case 'daily':
    case 'every_n_days':
    case 'weekly':
    case 'every_n_weeks':
    case 'monthly':
    case 'yearly':
      return anchor;

    case 'weekday': {
      const targets = getTaskWeekdays(task);
      return snapToMatchingWeekday(anchor, targets);
    }

    case 'monthly_date': {
      const monthDay = task.monthDay || 1;
      const d = new Date(anchor);
      if (d.getDate() <= monthDay) {
        d.setDate(monthDay);
      } else {
        d.setMonth(d.getMonth() + 1);
        d.setDate(monthDay);
      }
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    }

    default:
      return anchor;
  }
};

// Given a recurring task with an explicit deadline, snap the deadline forward
// to the first valid occurrence according to the repeat pattern.
// This handles the case where someone changed weekdays after creating the task —
// the old deadline might no longer match the new pattern.
export const snapDeadlineToPattern = (task, dateStr) => {
  if (!task.repeatType || task.repeatType === 'none') return dateStr;
  if (task.repeatType === 'weekday') {
    const targets = getTaskWeekdays(task);
    return snapToMatchingWeekday(dateStr, targets);
  }
  if (task.repeatType === 'monthly_date') {
    const monthDay = task.monthDay || 1;
    const d = new Date(dateStr);
    if (d.getDate() === monthDay) return dateStr;
    if (d.getDate() < monthDay) {
      d.setDate(monthDay);
    } else {
      d.setMonth(d.getMonth() + 1);
      d.setDate(monthDay);
    }
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }
  return dateStr;
};
